import type { ThreadMessage } from "./message-thread";

/**
 * Every row the thread has seen, oldest first.
 *
 * The transcript comes from two reads that move independently: a page of older
 * messages, fetched once when the reader scrolls back, and the newest page,
 * which slides forward as messages arrive. Concatenating them opens a hole --
 * the older page covers up to wherever the newest page began, and once that
 * page slides past its own start the rows in between belong to neither.
 *
 * Accumulating avoids the question entirely: nothing is ever dropped, so
 * nothing can fall between two windows. It is bounded by the length of one
 * conversation in one tab, which is a page of text.
 */
export function mergeThread<Message extends ThreadMessage>(
  seen: ReadonlyMap<string, Message>,
  incoming: readonly Message[],
): Map<string, Message> {
  const merged = new Map<string, Message>(seen);
  for (const message of incoming) {
    // The newer read wins: a row can change after it is first seen.
    merged.set(message.id, message);
  }
  return new Map(
    [...merged.values()]
      .sort((left, right) => {
        const byTime =
          new Date(left.createdAt).getTime() -
          new Date(right.createdAt).getTime();
        // Two rows written in the same millisecond would otherwise swap places
        // between polls and make the thread flicker.
        return byTime !== 0 ? byTime : left.id.localeCompare(right.id);
      })
      .map((message) => [message.id, message]),
  );
}
